import { useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";

type NoEmbedResponse = {
  title?: string;
  author_name?: string;
  provider_name?: string;
  thumbnail_url?: string;
  html?: string;
  error?: string;
};

const YT_REGEX =
  /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i;
const IG_REGEX = /instagram\.com\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i;
const LI_ACTIVITY_REGEX = /linkedin\.com\/.*?activity-([0-9]+)/i;

interface Props {
  url: string;
  youtubePreview?: "embed" | "thumbnail";
}

export function LinkPreview({ url, youtubePreview = "embed" }: Props) {
  const [noEmbed, setNoEmbed] = useState<NoEmbedResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ytVideoId = useMemo(() => {
    const match = url.match(YT_REGEX);
    return match ? match[1] : null;
  }, [url]);

  const igMatch = useMemo(() => url.match(IG_REGEX), [url]);
  const liActivityId = useMemo(() => {
    const match = url.match(LI_ACTIVITY_REGEX);
    return match ? match[1] : null;
  }, [url]);

  useEffect(() => {
    let active = true;
    setError(null);
    setNoEmbed(null);
    if (!url) return;

    // If we recognize provider with native embed, skip noembed
    if (ytVideoId || igMatch || liActivityId) return;

    setLoading(true);
    const controller = new AbortController();
    const fetchPreview = async () => {
      try {
        const resp = await fetch(
          `https://noembed.com/embed?url=${encodeURIComponent(url)}`,
          { signal: controller.signal }
        );
        const data: NoEmbedResponse = await resp.json();
        if (!active) return;
        if (data.error) {
          setError(data.error);
        } else {
          setNoEmbed(data);
        }
      } catch (e: any) {
        if (!active) return;
        setError(e?.message || "Failed to load preview");
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchPreview();
    return () => {
      active = false;
      controller.abort();
    };
  }, [url, ytVideoId, igMatch, liActivityId]);

  const renderEmbed = () => {
    if (!url) return null;

    // YouTube
    if (ytVideoId) {
      if (youtubePreview === "thumbnail") {
        return (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className={styles.youtubePreview}
            aria-label="Open YouTube video"
          >
            <img
              src={`https://i.ytimg.com/vi/${ytVideoId}/hqdefault.jpg`}
              alt="YouTube video thumbnail"
              className={styles.youtubeThumbnail}
            />
          </a>
        );
      }

      const embedSrc = `https://www.youtube.com/embed/${ytVideoId}`;
      return (
        <div className={styles.youtubePreview}>
          <iframe
            title="YouTube Preview"
            src={embedSrc}
            className={styles.youtubeFrame}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className={styles.youtubeLink}
            aria-label="Open YouTube video"
          />
        </div>
      );
    }

    // Instagram (post/reel/tv)
    if (igMatch) {
      const shortcode = igMatch[2];
      const embedSrc = `https://www.instagram.com/p/${shortcode}/embed`;

      // Instagram's default embed renders around 540x705.
      const baseWidth = 540;
      const baseHeight = 905;
      const boxSize = 500; // container target size (both width and height)
      const scale = Math.min(boxSize / baseWidth, boxSize / baseHeight);

      return (
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          <div
            style={{
              width: "100%",
              height: baseHeight,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            <iframe
              title="Instagram Preview"
              src={embedSrc}
              width={baseWidth}
              height={baseHeight}
              style={{ border: 0, borderRadius: 8 }}
              allowTransparency
              allow="encrypted-media; clipboard-write"
            />
          </div>
        </div>
      );
    }

    // LinkedIn activity posts (best-effort)
    if (liActivityId) {
      const embedSrc = `https://www.linkedin.com/embed/feed/update/urn:li:activity:${liActivityId}`;

      // LinkedIn default embed size is roughly 552x700
      const baseWidth = 552;
      const baseHeight = 700;
      const boxSize = 500; // container target size
      const scale = Math.min(boxSize / baseWidth, boxSize / baseHeight);

      return (
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          <div
            style={{
              width: baseWidth,
              height: baseHeight,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            <iframe
              title="LinkedIn Preview"
              src={embedSrc}
              width={baseWidth}
              height={baseHeight}
              style={{ border: 0, borderRadius: 8 }}
              allowFullScreen
            />
          </div>
        </div>
      );
    }

    // Fallback: noembed result
    if (loading) return <div>Loading preview…</div>;
    if (error) {
      return (
        <a href={url} target="_blank" rel="noreferrer">
          Open link
        </a>
      );
    }
    if (noEmbed?.html) {
      return (
        <div
          style={{ width: "100%" }}
          dangerouslySetInnerHTML={{ __html: noEmbed.html }}
        />
      );
    }
    if (noEmbed) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 12,
          }}
        >
          {noEmbed.thumbnail_url ? (
            <img
              src={noEmbed.thumbnail_url}
              alt={noEmbed.title || "preview"}
              style={{
                width: 96,
                height: 96,
                objectFit: "cover",
                borderRadius: 6,
              }}
            />
          ) : null}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>
              {noEmbed.title || "Link preview"}
            </div>
            <div style={{ color: "#6b7280", fontSize: 12 }}>
              {noEmbed.provider_name || "Unknown source"}
            </div>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 12 }}
            >
              {url}
            </a>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div
        style={{
          height: "500px",
          width: "100%",
        }}
      >
        {renderEmbed()}
      </div>
    </>
  );
}
